import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Leaf, ArrowRight } from 'lucide-react';
import { GROUNDING_TECHNIQUES } from '../data/content';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

export default function Grounding() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="grounding-screen">
      <SEO
        title="Grounding Exercises — Calm Down Tools UK"
        description="Four short grounding techniques to bring you back to the present when you feel panicked, dissociated or stuck in a flashback. Free, in your browser."
        path="/app/grounding"
      />
      <Link
        to="/app"
        data-testid="grounding-back"
        className="inline-flex items-center gap-1 mb-6"
        style={{ color: 'var(--tf-text)', textDecoration: 'none', fontSize: 14, fontWeight: 700 }}
      >
        <ChevronLeft size={20} /> Back
      </Link>

      <p className="eyebrow">FEELING OVERWHELMED?</p>
      <h1
        className="font-heading mt-3"
        style={{ fontSize: 'clamp(28px, 4vw, 36px)', lineHeight: 1.15, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        You&apos;re safe right now.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Try any one of these. They gently bring your nervous system back to the present. Take your
        time — there is no rush, and there is no wrong way to do this.
      </p>

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      <div className="mt-8 flex flex-col gap-4">
        {GROUNDING_TECHNIQUES.map((g) => (
          <div key={g.id} className="tf-card p-5" data-testid={`grounding-exercise-${g.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-purple)' }}
              >
                <Leaf size={18} />
              </div>
              <div>
                <p className="font-heading" style={{ fontSize: 17, color: 'var(--tf-text)' }}>
                  {g.title}
                </p>
                <p
                  className="font-body"
                  style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', marginTop: 1, letterSpacing: '0.02em' }}
                >
                  {g.duration}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {g.steps.map((s, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'var(--tf-purple-light)',
                      color: 'var(--tf-purple-dark)',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </div>
                  <p
                    className="font-body flex-1"
                    style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--tf-text-secondary)' }}
                  >
                    {s}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Link
        to="/app/trauma"
        data-testid="goto-trauma-from-grounding"
        className="mt-8 flex items-center gap-3 p-4 rounded-2xl"
        style={{ background: 'var(--tf-pink-soft)', textDecoration: 'none' }}
      >
        <div className="flex-1">
          <p className="font-body font-bold" style={{ fontSize: 13.5, color: 'var(--tf-text)' }}>
            Learn more about trauma responses
          </p>
          <p
            className="font-body"
            style={{ fontSize: 12, color: 'var(--tf-text-secondary)', marginTop: 2 }}
          >
            Videos, the 5 trauma responses, and UK specialist help.
          </p>
        </div>
        <ArrowRight size={16} color="var(--tf-purple)" />
      </Link>

      <div className="mt-10 pt-6" style={{ borderTop: '1px solid var(--tf-card-border)' }}>
        <p
          className="font-body font-bold text-center"
          style={{ fontSize: 12.5, color: 'var(--tf-purple-dark)', lineHeight: 1.55 }}
        >
          If you are thinking of harming yourself, please call Samaritans on{' '}
          <a href="tel:116123" className="tf-link">116 123</a> (24/7) — or 999 in immediate danger.
        </p>
      </div>
    </div>
  );
}
